import Vue from 'vue'
import Router from 'vue-router'

import * as T from '@/types'

import store from '@/store/store'
import config from '@/config.ts'

import _get from 'lodash/get'

import ViewApp from '@/views/ViewApp.vue'

const ViewCases = () => import('@/views/cases/ViewCases.vue')
const ViewFiles = () => import('@/views/files/ViewFiles.vue')
const ViewSingleFile = () => import('@/views/files/ViewSingleFile.vue')

const ViewLogin = () => import('@/views/auth/ViewLogin.vue')
const ViewAccessCodeLogin = () => import('@/views/auth/ViewAccessCodeLogin.vue')
const ViewPasswordReset = () => import('@/views/auth/ViewPasswordReset.vue')
const ViewPasswordResetRequestForm = () => import('@/views/auth/ViewPasswordResetRequestForm.vue')

const ViewSettings = () => import('@/views/settings/ViewSettings.vue')
const ViewSettingsFTP = () => import('@/views/settings/ViewSettingsFTP.vue')

const ViewNotFound = () => import('@/views/ViewNotFound.vue')
const ViewTechnicalDifficulties = () => import('@/views/ViewTechnicalDifficulties.vue')

Vue.use(Router)

const router: Router = new Router({
    mode: 'history',

    base: process.env.BASE_URL,

    routes: [

        ////////////////////////////////////////////////////////////////
        // Authentication.
        ////////////////////////////////////////////////////////////////

        {
            path: '/login',
            name: T.CrmRoute.AUTH_LOGIN,
            component: ViewLogin,
            meta: {
                accessMode: T.CrmRouteAccessMode.UNAUTHORIZED_ONLY,
                requiresInitialisation: false,
            },
        },
        {
            path: '/access-code-login',
            name: T.CrmRoute.AUTH_ACCESS_CODE_LOGIN,
            component: ViewAccessCodeLogin,
            meta: {
                accessMode: T.CrmRouteAccessMode.UNAUTHORIZED_ONLY,
                requiresInitialisation: false,
            },
            beforeEnter: (to, from, next) => {
                if (from.name !== T.CrmRoute.AUTH_LOGIN 
                    && from.name !== T.CrmRoute.VIEW_SINGLE_FILE
                    && store.getters['auth/hasAccount']) {
                    console.log(`User has an account - redirecting to ${T.CrmRoute.AUTH_LOGIN}.`)
                    next({name: T.CrmRoute.AUTH_LOGIN, query: to.query })
                } else {
                    next()
                }
            },
        },
        {
            path: '/password-reset',
            name: T.CrmRoute.AUTH_PASSWORD_RESET,
            component: ViewPasswordResetRequestForm,
            meta: {
                accessMode: T.CrmRouteAccessMode.UNAUTHORIZED_ONLY,
                requiresInitialisation: false,
            },
        },
        {
            path: '/reset-password',
            name: T.CrmRoute.AUTH_PASSWORD_RESET_CONFIRMED,
            component: ViewPasswordReset,
            meta: {
                accessMode: T.CrmRouteAccessMode.UNAUTHORIZED_ONLY,
                requiresInitialisation: false,
            },
        },

        ////////////////////////////////////////////////////////////////
        // Single File (Unauthenticated)
        ////////////////////////////////////////////////////////////////

        {
            path: '/get-file',
            name: T.CrmRoute.VIEW_SINGLE_FILE,
            component: ViewSingleFile,
            props: (route) => ({ 
                email: route.query.email,
                postcode: route.query.postcode,
                accessCode: route.query.accessCode,
            }),
            meta: {
                accessMode: T.CrmRouteAccessMode.UNAUTHORIZED_ONLY,
                requiresInitialisation: false,
            },
        },

        ////////////////////////////////////////////////////////////////
        // Authenticated routes
        ////////////////////////////////////////////////////////////////
        
        {
            path: '/',
            component: ViewApp,
            redirect: { name: T.CrmRoute.ACCOUNT_HOME },
            children: [

                {
                    path: 'home',
                    name: T.CrmRoute.ACCOUNT_HOME,
                    component: ViewCases,
                    redirect: { name: T.CrmRoute.VIEW_FILES },
                },
                {
                    path: 'files',
                    name: T.CrmRoute.VIEW_FILES,
                    component: ViewFiles,
                },
                {
                    path: 'cases',
                    name: T.CrmRoute.VIEW_CASES,
                    component: ViewCases,
                },

                ////////////////////////////////////////////////////////////////
                // settings
                ////////////////////////////////////////////////////////////////
                
                {
                    path: 'settings',
                    component: ViewSettings,
                    name: T.CrmRoute.VIEW_SETTINGS,
                    redirect: { name: T.CrmRoute.VIEW_SETTINGS_FTP },
                    children: [

                        {
                            path: 'ftp',
                            component: ViewSettingsFTP,
                            name: T.CrmRoute.VIEW_SETTINGS_FTP,
                        },
                        // {
                        //     path: 'payment',
                        //     component: ViewFiles,
                        //     name: T.CrmRoute.VIEW_SETTINGS_PAYMENT,
                        // },

                    ],
                },
    
            ],
        },

        ////////////////////////////////////////////////////////////////
        // 404
        ////////////////////////////////////////////////////////////////

        {
            path: '/not-found',
            name: T.CrmRoute.NOT_FOUND,
            component: ViewNotFound,
            meta: {
                accessMode: T.CrmRouteAccessMode.ALWAYS_ACCESSIBLE,
                requiresInitialisation: false,
            },
        },

        {
            path: '/technical-difficulties',
            name: T.CrmRoute.TECHNICAL_DIFFICULTIES,
            component: ViewTechnicalDifficulties,
            meta: {
                accessMode: T.CrmRouteAccessMode.ALWAYS_ACCESSIBLE,
                requiresInitialisation: false,
            },
        },

        {
            path: '*',
            component: ViewNotFound,
        },
    
    ],

})

router.beforeEach(async (to, from, next) => {
    store.commit('auth/setResolvingRoute', true)

    console.groupCollapsed('router.beforeEach')

    console.log(`Navigating from:`, from)
    console.log(`Navigating to:`, to)

    ////////////////////////////////////////////////////////////////
    // Inspect token.
    ////////////////////////////////////////////////////////////////

    const loggedIn = store.getters['auth/token']
    // const loggedIn = await store.dispatch('auth/inspectToken', { doNotChangeRoute: true }, { root: true })

    ////////////////////////////////////////////////////////////////
    // Handle authorization-agnostic routes.
    ////////////////////////////////////////////////////////////////

    if (to.meta && to.meta.accessMode === T.CrmRouteAccessMode.ALWAYS_ACCESSIBLE) {
        next()

        console.groupEnd()
        store.commit('auth/setResolvingRoute', false)
        return
    }

    ////////////////////////////////////////////////////////////////
    // Handle unauthorized-only routes.
    ////////////////////////////////////////////////////////////////

    const unauthorizedOnly = to.meta && to.meta.accessMode === T.CrmRouteAccessMode.UNAUTHORIZED_ONLY
    if (unauthorizedOnly) {
        if (loggedIn) {
            console.log(`User is already logged in. Redirecting to default authorized route.`)
            next({ name: config.ROUTE.DEFAULT_AUTHORIZED_ROUTE_NAME })
        }
        else {
            console.log(`User does not need to be logged in. Continuing to ${to.name}.`)
            next()
        }

        console.groupEnd()
        store.commit('auth/setResolvingRoute', false)
        return
    }

    ////////////////////////////////////////////////////////////////
    // Handle authorized-only routes → logged in.
    ////////////////////////////////////////////////////////////////

    console.log(`This route requires authorization...`)

    if (loggedIn) {
        console.log(`User is logged in.`)

        const initialised = store.getters['auth/initialised']

        if (!initialised) {
            console.log(`Not initialised yet. Initialising now...`)
            try {
                await store.dispatch('auth/initialise', null, { root: true })
            }
            catch (error) {
                if (_get(error, 'response.status') === 402) {
                    console.log(`Payment required.`)
                }
                else if (_get(error, 'response.status') === 401) {
                    console.log(`Unauthorised - logging out.`)
                    console.groupEnd()
                    store.dispatch('auth/logout')
                }
                else {
                    console.log(`An error occurred during initialisation.`)
                    console.log(`Redirecting to status page.`)
                    next({ name: T.CrmRoute.TECHNICAL_DIFFICULTIES })

                    console.groupEnd()
                    store.commit('auth/setResolvingRoute', false)
                    return
                }
            }
            console.log(`Initialised.`)
        }

        next()

        console.groupEnd()
        store.commit('auth/setResolvingRoute', false)
        return
    }

    ////////////////////////////////////////////////////////////////
    // Handle authorized-only routes → not logged in.
    ////////////////////////////////////////////////////////////////

    if (!loggedIn) {
        console.log(`User is not logged in. Redirecting to default unauthorized route.`)
        next({ name: config.ROUTE.DEFAULT_UNAUTHORIZED_ROUTE_NAME, query: { path: to.fullPath } })

        console.groupEnd()
        store.commit('auth/setResolvingRoute', false)
        return
    }
})

export default router
